<template>
  <div class="excel-searcher">
    <div class="header">
      <h2>🔍 Поиск и извлечение данных из Excel</h2>
    </div>

    <div class="controls">
      <div class="input-group">
        <label>Текст для поиска:</label>
        <input 
          v-model="searchText" 
          type="text" 
          placeholder="Введите текст для поиска..."
          class="search-input"
          @keyup.enter="processFiles"
        />
      </div>

      <div class="file-section">
        <input 
          ref="fileInput"
          type="file" 
          accept=".xlsx,.xls,.xlsm" 
          multiple 
          @change="handleFileSelect"
          class="file-input"
        />
        <button @click="$refs.fileInput.click()" class="btn btn-primary">
          📁 Выбрать Excel файлы ({{ selectedFiles.length }})
        </button>
      </div>

      <button 
        @click="processFiles" 
        :disabled="!canProcess"
        class="btn btn-success"
      >
        <span v-if="processing">⏳ Обработка...</span>
        <span v-else>🔍 Найти и извлечь данные</span>
      </button>
    </div>

    <div v-if="selectedFiles.length" class="files-info">
      <h3>📁 Выбранные файлы:</h3>
      <div class="files-list">
        <div v-for="file in selectedFiles" :key="file.name" class="file-tag">
          {{ file.name }}
        </div>
      </div>
    </div>

    <div v-if="processing" class="progress-section">
      <div class="progress-bar-container">
        <div class="progress-bar" :style="{ width: progress + '%' }"></div>
      </div>
      <div class="progress-text">{{ progress }}% — {{ currentFile }}</div>
    </div>

    <div v-if="debugInfo.length > 0" class="debug-section">
      <h4>Отладка: поиск для строки {{ debugRow }}</h4>
      <div class="debug-list">
        <div v-for="(info, idx) in debugInfo" :key="idx" class="debug-item" :class="{ 'is-time': info.isTime, 'is-day': info.isDay }">
          {{ info.desc }}: "{{ info.value }}" (тип: {{ info.type }})
          <span v-if="info.isTime">⏰ ВРЕМЯ!</span>
          <span v-if="info.isDay">📅 ДАТА!</span>
        </div>
      </div>
    </div>

    <div v-if="results.length > 0 && !processing" class="results">
      <h3>📊 Найдено записей: {{ results.length }}</h3>
      
      <div class="preview-section">
        <h4>Предпросмотр (первые 5 записей):</h4>
        <div class="table-wrapper">
          <table class="preview-table">
            <thead>
              <tr>
                <th>Найденный текст</th>
                <th>Ячейка выше</th>
                <th>Ячейка 4-я строка</th>
                <th>Время</th>
                <th>Дата</th>
                <th>Источник</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in results.slice(0, 5)" :key="idx">
                <td>{{ row.foundText }}</td>
                <td>{{ row.cellAbove }}</td>
                <td>{{ row.cellRow4 }}</td>
                <td>{{ row.timeValue }}</td>
                <td>{{ row.dateValue }}</td>
                <td class="source-cell">{{ row.source }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <button @click="downloadResultFile" class="btn btn-download-big">
        💾 Скачать результат ({{ results.length }} записей)
      </button>
    </div>

    <div v-if="error" class="error">
      ❌ {{ error }}
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';

export default {
  name: 'ExcelSearcher',
  
  data() {
    return {
      searchText: '',
      selectedFiles: [],
      processing: false,
      progress: 0,
      currentFile: '',
      results: [],
      error: null,
      debugInfo: [],
      debugRow: -1
    }
  },

  computed: {
    canProcess() {
      return this.searchText.trim() && this.selectedFiles.length > 0 && !this.processing;
    }
  },

  methods: {
    handleFileSelect(event) {
      this.selectedFiles = Array.from(event.target.files);
      this.results = [];
      this.error = null;
      this.debugInfo = [];
      this.debugRow = -1;
    },

    // Проверяем, является ли значением времени
    isTimeValue(value) {
      if (value === null || value === undefined) return false;
      
      // Date объект
      if (value instanceof Date) {
        const hours = value.getHours();
        const minutes = value.getMinutes();
        const timeStr = `${hours}:${String(minutes).padStart(2, '0')}`;
        return ['8:00', '9:30', '11:00', '13:00', '14:30', '16:00', '17:30'].includes(timeStr);
      }
      
      // Число (Excel serial)
      if (typeof value === 'number') {
        const timeValues = [0.3333333333333333, 0.3958333333333333, 0.4583333333333333, 
                           0.5416666666666666, 0.6041666666666666, 0.6666666666666666, 
                           0.7291666666666666];
        return timeValues.some(time => Math.abs(time - value) < 0.0001);
      }
      
      // Строка
      if (typeof value === 'string') {
        const str = value.trim();
        return ['8:00', '9:30', '11:00', '13:00', '14:30', '16:00', '17:30'].includes(str);
      }
      
      return false;
    },

    // Проверяем, является ли значением даты
    isDateValue(value) {
      if (value instanceof Date) return true;
      if (typeof value === 'number' && value > 40000 && value < 60000) return true;
      return false;
    },

    // Форматируем время для отображения
    formatTime(value) {
      if (value instanceof Date) {
        const hours = value.getHours();
        const minutes = value.getMinutes();
        return `${hours}:${String(minutes).padStart(2, '0')}`;
      }
      
      if (typeof value === 'number') {
        const totalMinutes = Math.round(value * 24 * 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}:${String(minutes).padStart(2, '0')}`;
      }
      
      return String(value);
    },

    // Форматируем дату в формат DD.MM.YYYY
    formatDateToDisplay(value) {
      if (value instanceof Date) {
        const day = String(value.getDate()).padStart(2, '0');
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const year = value.getFullYear();
        return `${day}.${month}.${year}`;
      }
      
      if (typeof value === 'number') {
        const date = XLSX.SSF.parse_date_code(value);
        if (date) {
          const day = String(date.d).padStart(2, '0');
          const month = String(date.m).padStart(2, '0');
          const year = date.y;
          return `${day}.${month}.${year}`;
        }
      }
      
      // Если это строка, пробуем распарсить
      if (typeof value === 'string') {
        const parsed = this.parseDateString(value);
        if (parsed) return parsed;
      }
      
      return String(value);
    },

    // Парсим строку с датой
    parseDateString(dateStr) {
      if (!dateStr || typeof dateStr !== 'string') return null;
      
      const patterns = [
        /(\d{4})-(\d{2})-(\d{2})/, // 2026-02-23
        /(\d{2})\.(\d{2})\.(\d{4})/, // 23.02.2026
        /(\d{2})\/(\d{2})\/(\d{4})/, // 23/02/2026
      ];
      
      for (let pattern of patterns) {
        const match = dateStr.match(pattern);
        if (match) {
          let year, month, day;
          if (pattern.source.includes('-')) {
            year = match[1];
            month = match[2];
            day = match[3];
          } else {
            day = match[1];
            month = match[2];
            year = match[3];
          }
          return `${day}.${month}.${year}`;
        }
      }
      
      return null;
    },

    // Получаем значение ячейки как строку (с учётом объединённых ячеек)
    getCellValue(worksheet, row, col) {
      let cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      let cell = worksheet[cellAddress];
      
      // Если ячейка пустая, проверяем объединённые ячейки
      if (!cell && worksheet['!merges']) {
        for (let merge of worksheet['!merges']) {
          if (row >= merge.s.r && row <= merge.e.r && 
              col >= merge.s.c && col <= merge.e.c) {
            cellAddress = XLSX.utils.encode_cell({ r: merge.s.r, c: merge.s.c });
            cell = worksheet[cellAddress];
            break;
          }
        }
      }
      
      if (!cell) return '';
      
      const value = cell.v;
      
      if (this.isTimeValue(value)) {
        return this.formatTime(value);
      }
      
      if (value instanceof Date) {
        return this.formatDateToDisplay(value);
      }
      
      return String(value || cell.w || '').trim();
    },

    // Получаем сырое значение ячейки
    getRawCellValue(worksheet, row, col) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = worksheet[cellAddress];
      return cell ? cell.v : undefined;
    },

    async processFiles() {
      this.processing = true;
      this.progress = 0;
      this.results = [];
      this.error = null;
      this.debugInfo = [];
      this.debugRow = -1;

      const searchLower = this.searchText.toLowerCase();

      try {
        for (let i = 0; i < this.selectedFiles.length; i++) {
          const file = this.selectedFiles[i];
          this.currentFile = file.name;
          
          await this.processFile(file, searchLower);
          
          this.progress = Math.round(((i + 1) / this.selectedFiles.length) * 100);
        }

        if (this.results.length === 0) {
          this.error = 'Совпадений не найдено ни в одном файле';
        }
      } catch (err) {
        this.error = 'Ошибка обработки: ' + err.message;
        console.error(err);
      } finally {
        this.processing = false;
        this.currentFile = '';
      }
    },

    async processFile(file, searchLower) {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { 
        type: 'array',
        cellDates: true,
        cellNF: true,
        cellStyles: true
      });

      workbook.SheetNames.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName];
        const ref = worksheet['!ref'] || 'A1';
        const range = XLSX.utils.decode_range(ref);

        // Ищем все ячейки с текстом
        for (let R = range.s.r; R <= range.e.r; R++) {
          for (let C = range.s.c; C <= range.e.c; C++) {
            const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = worksheet[cellAddress];

            if (cell && cell.v !== undefined && cell.v !== null) {
              let cellValue = String(cell.v).toLowerCase();
              
              // Проверяем совпадение
              if (cellValue.includes(searchLower)) {
                // Отладка для первого найденного
                if (this.debugInfo.length === 0) {
                  this.debugRow = R;
                }
                
                const rowData = this.extractRowData(worksheet, R, C, file.name, sheetName, range);
                this.results.push(rowData);
              }
            }
          }
        }
      });
    },

    extractRowData(worksheet, foundRow, foundCol, fileName, sheetName, range) {
      // 1. Найденный текст
      const foundText = this.getCellValue(worksheet, foundRow, foundCol);

      // 2. Ячейка выше на 1 в той же колонке
      let cellAbove = '';
      if (foundRow > 0) {
        cellAbove = this.getCellValue(worksheet, foundRow - 1, foundCol);
      }

      // 3. Ищем данные группы/аудитории в строках 4–7 (индексы 2–6)
      // Определяем нужную строку по ключевому слову "Группа" или "Аудитория" в ячейке слева
      let cellRow4 = '';
      const headerKeywords = ['группа', 'аудитория'];
      for (let r = 2; r <= 6; r++) {
        if (r < range.s.r || r > range.e.r) continue;
        if (foundCol > 0) {
          const leftValue = String(this.getCellValue(worksheet, r, foundCol - 1)).toLowerCase();
          if (headerKeywords.some(kw => leftValue.includes(kw))) {
            cellRow4 = this.getCellValue(worksheet, r, foundCol);
            break;
          }
        }
      }
      // Fallback: если не удалось определить по ключевым словам, берём строку 4
      if (!cellRow4 && range.s.r <= 3 && 3 <= range.e.r) {
        cellRow4 = this.getCellValue(worksheet, 3, foundCol);
      }

      // 4. Ищем время и дату
      let timeValue = '';
      let dateValue = '';
      
      // Идём вверх от найденной строки, ищем время в любой колонке
      for (let r = foundRow; r >= range.s.r; r--) {
        for (let c = range.s.c; c <= range.e.c; c++) {
          const rawValue = this.getRawCellValue(worksheet, r, c);
          if (this.isTimeValue(rawValue)) {
            timeValue = this.formatTime(rawValue);
            
            // Ищем дату слева от времени (та же строка, колонка c-1)
            if (c > range.s.c) {
              let leftCol = c - 1;
              let leftRawValue = this.getRawCellValue(worksheet, r, leftCol);
              
              // Если ячейка пустая, возможно она объединена
              if ((leftRawValue === undefined || leftRawValue === null || leftRawValue === '') && 
                  worksheet['!merges']) {
                for (let merge of worksheet['!merges']) {
                  if (r >= merge.s.r && r <= merge.e.r && 
                      leftCol >= merge.s.c && leftCol <= merge.e.c) {
                    const masterCell = XLSX.utils.encode_cell({ r: merge.s.r, c: merge.s.c });
                    const masterValue = worksheet[masterCell];
                    if (masterValue && masterValue.v !== undefined) {
                      leftRawValue = masterValue.v;
                    }
                    break;
                  }
                }
              }
              
              // Форматируем дату
              if (this.isDateValue(leftRawValue)) {
                dateValue = this.formatDateToDisplay(leftRawValue);
              } else if (leftRawValue && typeof leftRawValue === 'string') {
                const parsed = this.parseDateString(leftRawValue);
                if (parsed) {
                  dateValue = parsed;
                }
              }
            }
            
            // Отладка
            if (this.debugInfo.length < 6) {
              this.debugInfo.push({
                desc: `Время: строка ${r}, кол ${c}`,
                value: timeValue,
                type: typeof rawValue,
                isTime: true,
                isDay: false
              });
              
              if (c > range.s.c) {
                this.debugInfo.push({
                  desc: `  Дата слева (кол ${c - 1})`,
                  value: dateValue || 'не найдена',
                  type: 'date',
                  isTime: false,
                  isDay: true
                });
              }
            }
            
            break;
          }
        }
        if (timeValue) break;
        if (foundRow - r > 10) break;
      }

      // 5. Источник
      const source = `${fileName} | ${sheetName}`;

      return {
        foundText,
        cellAbove,
        cellRow4,
        timeValue,
        dateValue: dateValue || 'Дата не найдена',
        source
      };
    },

    downloadResultFile() {
      if (this.results.length === 0) {
        alert('Нет данных для скачивания');
        return;
      }

      const workbook = XLSX.utils.book_new();
      
      const sheetData = [
        ['Найденный текст', 'Ячейка выше', 'Ячейка 4-я строка', 'Время', 'Дата', 'Источник']
      ];

      this.results.forEach(row => {
        sheetData.push([
          row.foundText,
          row.cellAbove,
          row.cellRow4,
          row.timeValue,
          row.dateValue,
          row.source
        ]);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

      worksheet['!cols'] = [
        { wch: 30 }, // A
        { wch: 20 }, // B
        { wch: 20 }, // C
        { wch: 15 }, // D
        { wch: 15 }, // E
        { wch: 50 }  // F
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Результаты поиска');

      const now = new Date();
      const timestamp = now.toISOString().slice(0,19).replace(/:/g, '-');
      const searchTextSafe = this.searchText.replace(/[^a-zA-Z0-9а-яА-Я]/g, '_').slice(0, 20);
      const fileName = `Extracted_${searchTextSafe}_${timestamp}.xlsx`;

      XLSX.writeFile(workbook, fileName, {
        bookType: 'xlsx',
        cellStyles: true
      });
    }
  }
}
</script>

<style scoped>
.excel-searcher {
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  min-height: 100vh;
  border-radius: 16px;
  box-shadow: 0 25px 50px rgba(0,0,0,0.3);
}

.header {
  text-align: center;
  margin-bottom: 40px;
}

.header h2 {
  color: white;
  font-size: 32px;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
}

.controls {
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  margin-bottom: 25px;
}

.input-group {
  margin-bottom: 25px;
}

.input-group label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.search-input {
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #e1e8ed;
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.3s;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: #2a5298;
  box-shadow: 0 0 0 3px rgba(42, 82, 152, 0.1);
}

.file-section {
  margin-bottom: 25px;
}

.file-input {
  display: none;
}

.btn {
  padding: 14px 28px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  width: 100%;
  justify-content: center;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.btn-success {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  width: 100%;
  justify-content: center;
  margin-top: 10px;
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(17, 153, 142, 0.4);
}

.btn-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-download-big {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  width: 100%;
  justify-content: center;
  padding: 18px 32px;
  font-size: 18px;
  margin-top: 20px;
}

.btn-download-big:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(240, 147, 251, 0.5);
}

.files-info {
  background: white;
  padding: 25px;
  border-radius: 16px;
  margin-bottom: 25px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.files-info h3 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 18px;
}

.files-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.file-tag {
  background: #f0f4f8;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.progress-section {
  background: white;
  padding: 25px;
  border-radius: 16px;
  margin-bottom: 25px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.progress-bar-container {
  height: 12px;
  background: #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 6px;
  transition: width 0.3s ease;
}

.progress-text {
  text-align: center;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
}

.debug-section {
  background: #fffbeb;
  padding: 20px;
  border-radius: 16px;
  margin-bottom: 25px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  border: 2px solid #f59e0b;
}

.debug-section h4 {
  margin: 0 0 15px 0;
  color: #92400e;
  font-size: 16px;
  font-weight: 600;
}

.debug-list {
  font-family: monospace;
  font-size: 12px;
  max-height: 500px;
  overflow-y: auto;
}

.debug-item {
  padding: 6px 8px;
  border-bottom: 1px solid #fcd34d;
  color: #78350f;
}

.debug-item.is-time {
  background: #fef3c7;
  font-weight: bold;
  color: #92400e;
}

.debug-item.is-day {
  background: #dbeafe;
  font-weight: bold;
  color: #1e40af;
}

.results {
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
}

.results h3 {
  margin: 0 0 25px 0;
  color: #2c3e50;
  font-size: 24px;
  text-align: center;
}

.preview-section h4 {
  margin: 0 0 15px 0;
  color: #4a5568;
  font-size: 16px;
  font-weight: 600;
}

.table-wrapper {
  overflow-x: auto;
  margin-bottom: 25px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.preview-table th {
  background: #f7fafc;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #2d3748;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}

.preview-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  color: #4a5568;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-table tr:hover {
  background: #f7fafc;
}

.source-cell {
  font-size: 12px;
  color: #718096;
}

.error {
  background: #fed7d7;
  color: #c53030;
  padding: 20px;
  border-radius: 12px;
  margin-top: 20px;
  border-left: 4px solid #c53030;
  font-weight: 500;
}
</style>